/**
 * DirectoryPage — S-DIR, the authenticated landing (UX-RW-03 v1.1 S-DIR;
 * RW-01 S1, S3, S4; ADR-005 v1.6 §3.1).
 *
 * - Empty input shows NO suggestion panel: the server-paged browse of the
 *   directory is shown instead (first page, then "Load more").
 * - From the first typed digit the suggestions appear: at most 50, verbatim,
 *   in the order the server sent them, with the typed prefix emphasised
 *   (presentation only). Fewer than 50 are never padded; at 50 with more on
 *   the server a truncation line says so.
 * - Selecting a TPIN (Enter or click) opens `/tpin/{tin}`.
 * - Nothing here refreshes by itself: a read happens only on typing, Load more
 *   or Retry (PD-33). There is no timer and no focus/visibility listener.
 *
 * Combobox contract (UX-T22 §7.3): role=combobox + aria-expanded +
 * aria-controls, role=option rows, result-count changes announced politely.
 */

import { useEffect, useId, useState } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'
import { AppHeader } from '@components/AppHeader'
import { Link } from '@components/Link'
import { navigateTo, useRoute } from '@components/route'
import { moveHighlight, normalizeTpinInput, splitPrefix, SUGGESTION_LIMIT } from '@domain/tpinInput'
import { tpinPath } from '@domain/tpinPaths'
import { useDirectoryStore } from '@stores/directoryStore'

/** The suggestions of a list that has not arrived: nothing to highlight or select. */
const NO_TINS: readonly string[] = []

export function DirectoryPage() {
  const route = useRoute()
  const period = route.period
  const query = useDirectoryStore((s) => s.query)
  const view = useDirectoryStore((s) => s.view)
  const setQuery = useDirectoryStore((s) => s.setQuery)
  const loadMore = useDirectoryStore((s) => s.loadMore)
  const retry = useDirectoryStore((s) => s.retry)
  const cancel = useDirectoryStore((s) => s.cancel)

  const [open, setOpen] = useState(true)
  const [highlight, setHighlight] = useState(0)
  const listboxId = useId()
  const hintId = useId()

  // Landing on the directory starts from the browse; leaving cancels what is in flight.
  useEffect(() => {
    setQuery('')
    return cancel
  }, [setQuery, cancel])

  const typing = query !== ''
  // One guard for every ready-only region: a property read on it cannot be reached while loading or failed.
  const ready = view.kind === 'ready' ? view : null
  const tins = ready === null ? NO_TINS : ready.list.tins
  const panelOpen = typing && open

  function select(tin: string): void {
    navigateTo(tpinPath(tin), period)
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    // Typing implies focus, and focus already opens the panel: no separate open here.
    setQuery(normalizeTpinInput(event.target.value))
    setHighlight(0)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setHighlight((current) => moveHighlight(current, 1, tins.length))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlight((current) => moveHighlight(current, -1, tins.length))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const choice = typing ? tins[highlight] : undefined
      if (choice !== undefined) select(choice)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setQuery('')
      setHighlight(0)
    }
  }

  const activeId = panelOpen && tins[highlight] !== undefined ? `${listboxId}-${tins[highlight]}` : undefined

  return (
    <div className="page" data-testid="directory-page">
      <AppHeader period={period} />
      <main className="page-main directory-main">
        <h1 className="directory-title">TPIN directory</h1>
        <p className="page-scope">Find a customer by TPIN to see its sync health.</p>

        <div className="directory-search">
          <label htmlFor={`${listboxId}-input`} className="directory-label">
            TPIN
          </label>
          <input
            id={`${listboxId}-input`}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            role="combobox"
            aria-expanded={panelOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            aria-describedby={hintId}
            placeholder="e.g. 1000117278"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            className="directory-input"
            data-testid="directory-input"
          />
          <p id={hintId} className="directory-hint" data-testid="directory-hint">
            Enter a TPIN (numbers only)
          </p>

          {panelOpen && (
            <div className="directory-suggestions" data-testid="directory-suggestions">
              {view.kind === 'loading' && (
                <p role="status" className="directory-note" data-testid="directory-searching">
                  Searching…
                </p>
              )}
              {view.kind === 'error' && (
                <div role="alert" className="directory-note directory-error" data-testid="directory-error">
                  <p>{view.message}</p>
                  <button type="button" className="button-retry" onClick={retry} onMouseDown={(e) => e.preventDefault()} data-testid="directory-retry">
                    Retry
                  </button>
                </div>
              )}
              {ready !== null && ready.list.tins.length === 0 && (
                <div role="status" className="directory-note" data-testid="directory-zero">
                  <p>
                    No TPINs match <strong>{query}</strong>
                  </p>
                  <p className="directory-hint">Check the digits — TPINs are numbers only</p>
                </div>
              )}
              {ready !== null && ready.list.tins.length > 0 && (
                <>
                  <p role="status" aria-live="polite" className="visually-hidden" data-testid="directory-count">
                    {ready.list.tins.length} {ready.list.tins.length === 1 ? 'match' : 'matches'}
                  </p>
                  <ul id={listboxId} role="listbox" aria-label="Matching TPINs" className="directory-options" data-testid="directory-options">
                    {tins.map((tin, index) => {
                      const { prefix, rest } = splitPrefix(tin, query)
                      return (
                        <li
                          key={tin}
                          id={`${listboxId}-${tin}`}
                          role="option"
                          aria-selected={index === highlight}
                          className={`directory-option${index === highlight ? ' directory-option--active' : ''}`}
                          onMouseDown={(event) => event.preventDefault()}
                          onMouseEnter={() => setHighlight(index)}
                          onClick={() => select(tin)}
                          data-testid={`directory-option-${tin}`}
                        >
                          <span className="directory-tin">
                            <strong className="directory-prefix" data-testid={`directory-prefix-${tin}`}>{prefix}</strong>
                            {rest}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                  {ready.list.hasMore && (
                    <p className="directory-note directory-hint" data-testid="directory-truncated">
                      Showing first {SUGGESTION_LIMIT} matches — keep typing to narrow
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {!typing && (
          <section className="directory-browse" aria-labelledby={`${listboxId}-browse`} data-testid="directory-browse">
            <h2 id={`${listboxId}-browse`} className="directory-browse-title">
              All TPINs
            </h2>
            {view.kind === 'loading' && (
              <p role="status" className="directory-note" data-testid="directory-browse-loading">
                Loading TPINs…
              </p>
            )}
            {view.kind === 'error' && (
              <div role="alert" className="directory-note directory-error" data-testid="directory-browse-error">
                <p>{view.message}</p>
                <button type="button" className="button-retry" onClick={retry} data-testid="directory-browse-retry">
                  Retry
                </button>
              </div>
            )}
            {ready !== null && ready.list.tins.length === 0 && (
              <p role="status" className="directory-note" data-testid="directory-browse-empty">
                No TPINs are registered.
              </p>
            )}
            {ready !== null && ready.list.tins.length > 0 && (
              <>
                <p role="status" aria-live="polite" className="directory-hint" data-testid="directory-browse-count">
                  Showing {ready.list.tins.length} {ready.list.tins.length === 1 ? 'TPIN' : 'TPINs'}
                </p>
                <ul className="directory-rows" data-testid="directory-rows">
                  {ready.list.tins.map((tin) => (
                    <li key={tin} className="directory-row">
                      <Link to={tpinPath(tin)} period={period} className="directory-row-link" testid={`directory-row-${tin}`}>
                        <span className="directory-tin">{tin}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                {ready.more.kind === 'error' && (
                  <div role="alert" className="directory-note directory-error" data-testid="directory-more-error">
                    <p>{ready.more.message}</p>
                  </div>
                )}
                {ready.list.hasMore && (
                  <button
                    type="button"
                    className="next-page-button"
                    onClick={loadMore}
                    disabled={ready.more.kind === 'loading'}
                    data-testid="directory-load-more"
                  >
                    {ready.more.kind === 'loading' ? 'Loading…' : ready.more.kind === 'error' ? 'Retry loading more' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </section>
        )}
      </main>
    </div>
  )
}
